import {
  ExclamationMarkCircleFilled,
  ExclamationMarkTriangleFilled,
} from "components/icons/generated";
import Box from "ds/components/Box";
import Icon from "ds/components/Icon";
import TableCellEmpty from "ds/components/Table/Row/CellEmpty";
import TableSimpleCell from "ds/components/TableSimple/Cell";
import { TableSimpleCellProps } from "ds/components/TableSimple/types";
import Tooltip from "ds/components/Tooltip";
import Typography from "ds/components/Typography";
import { ApiKey, ApiKeyDisabledReason } from "types/generated";
import { formatDate } from "utils/date";
import { unixTimestampToMs } from "utils/time";

import { getPersonalApiKeyIssue, PersonalApiKeyIssue } from "../../helpers";

type PersonalApiKeysTableIssuesCellItem = Pick<
  ApiKey,
  "disabled" | "disabledAt" | "disabledReason" | "expiresAt"
>;

const ISSUE_CONFIG = {
  REVOKED: { label: "Revoked", icon: ExclamationMarkCircleFilled, color: "danger" },
  EXPIRED: { label: "Expired", icon: ExclamationMarkTriangleFilled, color: "warning" },
} as const satisfies Record<PersonalApiKeyIssue, object>;

const DISABLED_REASON_TOOLTIPS: Record<ApiKeyDisabledReason, string> = {
  [ApiKeyDisabledReason.Admin]: "Revoked by an organization admin",
  [ApiKeyDisabledReason.Account]: "Disabled when personal API keys were turned off",
  [ApiKeyDisabledReason.Deprovisioned]: "Owner was deprovisioned",
  [ApiKeyDisabledReason.Scheme]: "Disabled after the account moved to login policies",
};

const getPersonalApiKeyIssueTooltip = (
  issue: PersonalApiKeyIssue | undefined,
  apiKey: PersonalApiKeysTableIssuesCellItem
) => {
  if (issue === "REVOKED") {
    const reason = apiKey.disabledReason
      ? DISABLED_REASON_TOOLTIPS[apiKey.disabledReason]
      : "This key has been revoked";

    return apiKey.disabledAt
      ? `${reason} on ${formatDate({ date: unixTimestampToMs(apiKey.disabledAt) })}`
      : reason;
  }

  if (issue === "EXPIRED" && apiKey.expiresAt) {
    return `Expired on ${formatDate({ date: unixTimestampToMs(apiKey.expiresAt) })}`;
  }

  return undefined;
};

const PersonalApiKeysTableIssuesCell = ({
  item: apiKey,
  ...cellProps
}: TableSimpleCellProps<PersonalApiKeysTableIssuesCellItem>) => {
  const issue = getPersonalApiKeyIssue(apiKey);
  const config = issue && ISSUE_CONFIG[issue];
  const tooltip = getPersonalApiKeyIssueTooltip(issue, apiKey);

  return (
    <TableSimpleCell {...cellProps}>
      {config && (
        <Tooltip
          active={!!tooltip}
          on={(props) => (
            <Box {...props} direction="row" align="center" gap="small">
              <Icon src={config.icon} color={config.color} noShrink />
              <Typography tag="span" variant="p-body3" color={config.color}>
                {config.label}
              </Typography>
            </Box>
          )}
        >
          {tooltip}
        </Tooltip>
      )}
      {!config && <TableCellEmpty />}
    </TableSimpleCell>
  );
};

export default PersonalApiKeysTableIssuesCell;
